import { useState, useEffect } from "react";
import { usePopupsByPage } from "@/hooks/useFirestore";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import type { Popup } from "@shared/schema";
import { PopupDisplayFrequency, PopupStyle } from "@shared/schema";

interface PopupDisplayProps {
  pageId: string;
}

const POPUP_STORAGE_PREFIX = "popup_seen_";
const POPUP_DAILY_PREFIX = "popup_daily_";

// Stil bazlı renkler ve çerçeveler
const STYLE_CONFIG = {
  [PopupStyle.DEFAULT]: {
    border: "border-primary",
    background: "bg-gradient-to-br from-primary/5 to-primary/10",
    iconBg: "bg-primary/20",
    iconColor: "text-primary",
    buttonVariant: "default" as const,
  },
  [PopupStyle.SUCCESS]: {
    border: "border-green-500",
    background: "bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-950/30 dark:to-emerald-950/30",
    iconBg: "bg-green-100 dark:bg-green-900/50",
    iconColor: "text-green-600 dark:text-green-400",
    buttonVariant: "default" as const,
  },
  [PopupStyle.WARNING]: {
    border: "border-yellow-500",
    background: "bg-gradient-to-br from-yellow-50 to-amber-50 dark:from-yellow-950/30 dark:to-amber-950/30",
    iconBg: "bg-yellow-100 dark:bg-yellow-900/50",
    iconColor: "text-yellow-600 dark:text-yellow-400",
    buttonVariant: "default" as const,
  },
  [PopupStyle.INFO]: {
    border: "border-blue-500",
    background: "bg-gradient-to-br from-blue-50 to-cyan-50 dark:from-blue-950/30 dark:to-cyan-950/30",
    iconBg: "bg-blue-100 dark:bg-blue-900/50",
    iconColor: "text-blue-600 dark:text-blue-400",
    buttonVariant: "default" as const,
  },
  [PopupStyle.ANNOUNCEMENT]: {
    border: "border-purple-500",
    background: "bg-gradient-to-br from-purple-50 to-violet-50 dark:from-purple-950/30 dark:to-violet-950/30",
    iconBg: "bg-purple-100 dark:bg-purple-900/50",
    iconColor: "text-purple-600 dark:text-purple-400",
    buttonVariant: "default" as const,
  },
  [PopupStyle.PROMOTION]: {
    border: "border-pink-500",
    background: "bg-gradient-to-br from-pink-50 to-rose-50 dark:from-pink-950/30 dark:to-rose-950/30",
    iconBg: "bg-pink-100 dark:bg-pink-900/50",
    iconColor: "text-pink-600 dark:text-pink-400",
    buttonVariant: "default" as const,
  },
  [PopupStyle.CELEBRATION]: {
    border: "border-rainbow",
    background: "bg-gradient-to-br from-orange-50 via-pink-50 to-purple-50 dark:from-orange-950/30 dark:via-pink-950/30 dark:to-purple-950/30",
    iconBg: "bg-gradient-to-r from-orange-100 to-pink-100 dark:from-orange-900/50 dark:to-pink-900/50",
    iconColor: "text-pink-600 dark:text-pink-400",
    buttonVariant: "default" as const,
  },
};

export function PopupDisplay({ pageId }: PopupDisplayProps) {
  const { data: popups, isLoading } = usePopupsByPage(pageId);
  const [currentPopup, setCurrentPopup] = useState<Popup | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [dismissedIds, setDismissedIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    // Don't run if loading, no popups, or already showing a popup
    if (isLoading || !popups || popups.length === 0 || currentPopup !== null) return;

    // Sort popups by priority (highest first)
    const sortedPopups = [...popups].sort((a, b) => b.priority - a.priority);

    // Find the first popup that should be displayed
    for (const popup of sortedPopups) {
      if (shouldShowPopup(popup)) {
        setCurrentPopup(popup);
        setIsOpen(true);
        break;
      }
    }
  }, [popups, isLoading, currentPopup, dismissedIds]);

  const shouldShowPopup = (popup: Popup): boolean => {
    const { id, displayFrequency } = popup;

    // Don't show if already dismissed in this session
    if (dismissedIds.has(id)) {
      return false;
    }

    switch (displayFrequency) {
      case PopupDisplayFrequency.ONCE: {
        // Check if user has seen this popup before
        const seenKey = `${POPUP_STORAGE_PREFIX}${id}`;
        const hasSeen = localStorage.getItem(seenKey);
        return !hasSeen;
      }

      case PopupDisplayFrequency.DAILY: {
        // Check if user has seen this popup today
        const dailyKey = `${POPUP_DAILY_PREFIX}${id}`;
        const lastSeen = localStorage.getItem(dailyKey);
        
        if (!lastSeen) return true;

        const lastSeenDate = new Date(parseInt(lastSeen));
        const today = new Date();
        
        // Check if last seen was on a different day
        const isSameDay = 
          lastSeenDate.getFullYear() === today.getFullYear() &&
          lastSeenDate.getMonth() === today.getMonth() &&
          lastSeenDate.getDate() === today.getDate();
        
        return !isSameDay;
      }

      case PopupDisplayFrequency.ALWAYS:
      default:
        // Always show (but only once per session due to dismissedIds check)
        return true;
    }
  };

  const handleClose = () => {
    if (!currentPopup) return;

    const { id, displayFrequency } = currentPopup;

    // Mark as seen in localStorage based on display frequency FIRST
    if (displayFrequency === PopupDisplayFrequency.ONCE) {
      const seenKey = `${POPUP_STORAGE_PREFIX}${id}`;
      localStorage.setItem(seenKey, "true");
    } else if (displayFrequency === PopupDisplayFrequency.DAILY) {
      const dailyKey = `${POPUP_DAILY_PREFIX}${id}`;
      localStorage.setItem(dailyKey, Date.now().toString());
    }
    // For ALWAYS, we only track in dismissedIds (session-only)

    // Mark as dismissed in this session
    setDismissedIds(prev => new Set(prev).add(id));

    // Close the dialog and clear current popup
    setIsOpen(false);
    setCurrentPopup(null);
    
    // useEffect will now re-run and potentially show the next eligible popup
  };

  if (!currentPopup) return null;

  const style = currentPopup.style || PopupStyle.DEFAULT;
  const styleConfig = STYLE_CONFIG[style as keyof typeof STYLE_CONFIG] || STYLE_CONFIG[PopupStyle.DEFAULT];

  return (
    <Dialog 
      open={isOpen} 
      onOpenChange={(open) => {
        if (!open) {
          handleClose();
        }
      }}
    >
      <DialogContent 
        className={`max-w-lg border-2 ${styleConfig.border} ${styleConfig.background}`}
        data-testid={`popup-${currentPopup.id}`}
      >
        <DialogHeader>
          <div className="flex items-start gap-3">
            {currentPopup.icon && (
              <div className={`flex-shrink-0 w-12 h-12 rounded-full ${styleConfig.iconBg} flex items-center justify-center text-2xl`}>
                {currentPopup.icon}
              </div>
            )}
            <div className="flex-1">
              <DialogTitle className="text-xl" data-testid="popup-title">
                {currentPopup.title}
              </DialogTitle>
            </div>
          </div>
        </DialogHeader>
        
        <div className="py-4">
          {currentPopup.imageUrl && (
            <div className="mb-4">
              <img
                src={currentPopup.imageUrl}
                alt={currentPopup.title}
                className="w-full rounded-lg object-cover max-h-64 border-2 border-border"
                data-testid="popup-image"
              />
            </div>
          )}
          
          <DialogDescription
            className="text-base text-foreground"
            dangerouslySetInnerHTML={{ __html: currentPopup.content }}
            data-testid="popup-content"
          />
        </div>

        <DialogFooter>
          <Button 
            onClick={handleClose} 
            variant={styleConfig.buttonVariant}
            className="w-full"
            data-testid="popup-close-button"
          >
            {currentPopup.buttonText}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
